// src/services/photoLikeService.ts
import { getAuthHeaders } from '../utils/auth';

const API_BASE_URL = process.env.REACT_APP_API_URL;

export interface PhotoLikeResponse {
   message: string;
   likeCount: number;
   isLiked: boolean;
}

export interface PhotoLikeContext {
   tripId?: string;
   eventId?: string;
   albumId?: string;
   // New contextual like support
   type?: 'collection' | 'trip' | 'event' | 'album' | 'original';
   id?: string;
}

export interface PhotoLikeInfo {
   likeCount: number;
   isLiked?: boolean;
   likes: Array<{
      id: string;
      userId: string;
      photoId: string;
      likedAt: any;
   }>;
}

export class PhotoLikeService {
   /**
    * Like a photo with context
    */
   static async likePhoto(
      photoId: string,
      userId: string,
      context?: PhotoLikeContext
   ): Promise<PhotoLikeResponse> {
      try {
         console.log('PhotoLikeService: likePhoto called:', { photoId, userId, API_BASE_URL });

         const headers = await getAuthHeaders();
         console.log('PhotoLikeService: Got auth headers:', Object.keys(headers));

         const response = await fetch(`${API_BASE_URL}/api/photo-likes/${photoId}/like`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               ...headers,
            },
            body: JSON.stringify({
               userId,
               context: context?.type ? { type: context.type, id: context.id } : undefined,
            }),
         });

         console.log('PhotoLikeService: API response status:', response.status);

         const result = await response.json();
         console.log('PhotoLikeService: API response data:', result);

         if (!response.ok) {
            throw new Error(result.message || 'Failed to like photo');
         }

         return result;
      } catch (error) {
         console.error('PhotoLikeService: Error liking photo:', error);
         throw error;
      }
   }

   /**
    * Unlike a photo with context
    */
   static async unlikePhoto(
      photoId: string,
      userId: string,
      context?: PhotoLikeContext
   ): Promise<PhotoLikeResponse> {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(`${API_BASE_URL}/api/photo-likes/${photoId}/unlike`, {
            method: 'DELETE',
            headers: {
               'Content-Type': 'application/json',
               ...headers,
            },
            body: JSON.stringify({
               userId,
               context: context?.type ? { type: context.type, id: context.id } : undefined,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.message || 'Failed to unlike photo');
         }

         return result;
      } catch (error) {
         console.error('Error unliking photo:', error);
         throw error;
      }
   }

   /**
    * Get like information for a photo with context
    */
   static async getPhotoLikes(
      photoId: string,
      userId?: string,
      context?: PhotoLikeContext
   ): Promise<PhotoLikeInfo> {
      try {
         const headers = await getAuthHeaders();
         const queryParams = new URLSearchParams();

         if (userId) queryParams.append('userId', userId);
         if (context?.tripId) queryParams.append('tripId', context.tripId);
         if (context?.eventId) queryParams.append('eventId', context.eventId);
         if (context?.albumId) queryParams.append('albumId', context.albumId);
         // New contextual like support
         if (context?.type) queryParams.append('contextType', context.type);
         if (context?.id) queryParams.append('contextId', context.id);

         const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
         const response = await fetch(`${API_BASE_URL}/api/photo-likes/${photoId}${queryString}`, {
            method: 'GET',
            headers: {
               'Content-Type': 'application/json',
               ...headers,
            },
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.message || 'Failed to get photo likes');
         }

         return result;
      } catch (error) {
         console.error('Error getting photo likes:', error);
         throw error;
      }
   }

   /**
    * Toggle like status for a photo with context
    */
   static async toggleLike(
      photoId: string,
      userId: string,
      isCurrentlyLiked: boolean,
      context?: PhotoLikeContext
   ): Promise<PhotoLikeResponse> {
      if (isCurrentlyLiked) {
         return this.unlikePhoto(photoId, userId, context);
      } else {
         return this.likePhoto(photoId, userId, context);
      }
   }

   /**
    * Get all photos liked by a user
    */
   static async getUserLikedPhotos(userId: string): Promise<any[]> {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${API_BASE_URL}/api/photo-likes/user/${userId}/liked-photos`,
            {
               method: 'GET',
               headers: {
                  'Content-Type': 'application/json',
                  ...headers,
               },
            }
         );

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.message || 'Failed to get user liked photos');
         }

         return result.likedPhotos || [];
      } catch (error) {
         console.error('Error getting user liked photos:', error);
         throw error;
      }
   }
}
